import { FilterStorageService } from './../../filter-storage.service';
import { Permission } from './../../../models/permission.types';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError
} from 'rxjs';

import {
  AuthenticationResponseDto,
  ContractorRegisterRequestDto,
  CorporateAdminRegisterRequestDto,
  FacilityAdminRegisterRequestDto,
  LoginRequestDto,
  RegisterRequestDto,
  SocialLoginProvider,
  UserRole
} from '../../../models/auth.model';
import { UsersService } from '../../user.service';
import { FacebookHandlerService } from '../facebook/facebook-handler.service';
import { LinkedinHandlerService } from '../linkedin/linkedin-handler.service';
import { AuthAPIService } from './auth-api.service';
import {
  AUTH_LOCALSTORAGE_KEYS,
  AuthStorageService
} from './auth-storage.service';
import { TokenRefreshService } from './token-refresh.service';
import { WebPushService } from '../../push-notifications/web-push.service';
import { HttpErrorResponse } from '@angular/common/http';

/**
 * Handles authentication, session persistence, and user state management.
 * Provides login, logout, registration, social login, and token refresh operations.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly SESSION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

  private router = inject(Router);
  private authAPIService = inject(AuthAPIService);
  private authStorageService = inject(AuthStorageService);
  private linkedinService = inject(LinkedinHandlerService);
  private facebookService = inject(FacebookHandlerService);
  private userService = inject(UsersService);
  private tokenRefreshService = inject(TokenRefreshService);
  private filterStorageService = inject(FilterStorageService);
  private webPushService = inject(WebPushService);

  /**
   * Access token it's only story in Memory to avoid XSS attacks.
   * Every app should use the
   * ```ts
   * provideEnvironmentInitializer(() => refreshAccount()),
   * ```
   *
   * inside their providers to refresh the access token every time the app is loaded
   *
   * Refresh token is only store in a HttpCookie
   */
  private _accessToken = signal<string | null>(null);

  public get accessToken(): string | null {
    return this._accessToken() ?? null;
  }

  readonly authState = this.authStorageService.stateSignal;

  isAuthenticated(): boolean {
    return this.hasRecentSessionEvidence();
  }

  hasRecentSessionEvidence(): boolean {
    const fromState = this.authState().lastSessionAt;
    const fromStorage = this.authStorageService.getItem<string>(
      AUTH_LOCALSTORAGE_KEYS.lastSessionAt
    );

    const lastSessionAt = fromState ?? fromStorage;
    if (!lastSessionAt) {
      return false;
    }

    const lastSessionMs = Date.parse(lastSessionAt);
    if (Number.isNaN(lastSessionMs)) {
      return false;
    }

    return Date.now() - lastSessionMs <= AuthService.SESSION_MAX_AGE_MS;
  }

  socialLogin(
    provider: SocialLoginProvider,
    userRole: UserRole
  ): Observable<AuthenticationResponseDto> {
    let rxjsOperation: Observable<AuthenticationResponseDto | null>;

    if (provider === SocialLoginProvider.Facebook) {
      rxjsOperation = this.facebookService.initiateAuthFlow(userRole);
    } else if (provider === SocialLoginProvider.LinkedIn) {
      rxjsOperation = this.linkedinService.initiateAuthFlow(userRole);
    } else {
      return throwError(() => new Error('Unsupported social login provider'));
    }

    return rxjsOperation.pipe(
      tap((response) => {
        if (!response) {
          return null;
        }
        return this.applyAuthResponse(response);
      }),
      switchMap((response) => {
        if (!response) {
          return throwError(
            () =>
              new HttpErrorResponse({
                error: {
                  message:
                    "We couldn't connect with this provider. Please try again."
                }
              })
          );
        }
        return of(response);
      })
    );
  }

  /**
   * Logs in a user using email/password credentials.
   * @param req Login request payload.
   * @returns Observable with authentication response.
   */
  login(req: LoginRequestDto): Observable<AuthenticationResponseDto> {
    return this.authAPIService
      .login(req)
      .pipe(tap((res) => this.applyAuthResponse(res)));
  }

  /**
   * Validates the current access token and refreshes it if needed.
   * Call this on app initialization to restore the session if a valid refresh token exists.
   * @returns Observable with authentication response or null if no valid session exists.
   */
  validateDelegatedToken(
    token: string,
    facilityId: string
  ): Observable<AuthenticationResponseDto | null> {
    return this.authAPIService.verifyDelegatedToken(token.trim()).pipe(
      tap((res) => {
        if (res) {
          // Workaround to ensure the required facility is prioritized in the auth state
          this.applyAuthResponse({
            ...res,
            facilityAccess: {
              facilityId,
              facilityName: 'Required facility access',
              permissions: []
            }
          });
        }
      }),
      catchError(() =>
        throwError(
          () =>
            new Error(
              'The token is invalid or has expired. Redirecting to login.'
            )
        )
      )
    );
  }

  /**
   * Registers a new user.
   * @param req Registration request payload.
   * @returns Observable with authentication response.
   */
  register(req: RegisterRequestDto): Observable<AuthenticationResponseDto> {
    return this.authAPIService
      .register(req)
      .pipe(tap((res) => this.applyAuthResponse(res)));
  }

  registerContractor(
    req: ContractorRegisterRequestDto
  ): Observable<AuthenticationResponseDto> {
    return this.authAPIService.registerContractor(req).pipe(
      tap((v) => this._accessToken.set(v.accessToken)),
      switchMap((v) => {
        return this.userService.getMe().pipe(
          map((user) => {
            return {
              ...v,
              user
            } as AuthenticationResponseDto;
          })
        );
      }),
      tap((res) => this.applyAuthResponse(res))
    );
  }

  registerFacilityAdmin(
    req: FacilityAdminRegisterRequestDto
  ): Observable<AuthenticationResponseDto> {
    return this.authAPIService.registerFacilityAdmin(req).pipe(
      map(
        (v) =>
          ({
            ...v,
            user: {
              id: v.userId,
              email: v.email,
              firstName: req.firstName,
              lastName: req.lastName,
              role: 'FacilityAdmin'
            },
            facilityAccess: {
              facilityId: v.facilityId,
              facilityName: v.facilityName,
              permissions: []
            },
            accessToken: v.accessToken
          }) as AuthenticationResponseDto
      ),
      tap((res) => this._accessToken.set(res.accessToken))
    );
  }

  /**
   * Applies an auth response to establish the user session.
   * Call this explicitly after multi-step flows (e.g. onboarding) where
   * registration must complete before the session is initialized.
   */
  applyAuth(res: AuthenticationResponseDto): void {
    this.applyAuthResponse(res);
  }

  registerCorporateAdmin(
    req: CorporateAdminRegisterRequestDto
  ): Observable<AuthenticationResponseDto> {
    return this.authAPIService.registerCorporateAdmin(req).pipe(
      map((v) => {
        return {
          ...v,
          user: {
            id: v.userId,
            email: v.email,
            firstName: req.firstName,
            lastName: req.lastName,
            role: 'CorporateStaff'
          },

          accessToken: v.accessToken
        } as AuthenticationResponseDto;
      }),
      tap((res) => this._accessToken.set(res.accessToken))
    );
  }

  /**
   * Logs out the current user and clears session data.
   * @returns Observable that completes once logout is done.
   */
  logout(): Observable<void> {
    if (!this.accessToken) {
      return throwError(() => new Error('No active session'));
    }

    // Remove this device's push token first, while the auth token is still
    // valid, so the backend stops delivering notifications to it. Best-effort:
    // a failure here must not block logout.
    return this.webPushService.unregisterToken().pipe(
      catchError(() => of(null)),
      switchMap(() =>
        this.authAPIService.logout().pipe(catchError(() => of(void 0)))
      ),
      tap(() => this.closeSession()),
      map(() => void 0)
    );
  }

  clearSessionAndRedirect() {
    this.closeSession();
    this.router.navigateByUrl('/auth/login');
  }

  public handleRefreshFailure(error: HttpErrorResponse): void {
    // Keep session data for transient failures (offline, timeout, 5xx).
    // Clear only when backend explicitly rejects refresh.
    if (error.status === 401 || error.status === 403) {
      this.clearSessionAndRedirect();
    }
  }

  /**
   * Requests a password reset email for the given email address.
   * @param email User's email address.
   * @returns Observable that completes once the request is sent.
   */
  forgotPassword(email: string): Observable<void> {
    return this.authAPIService.forgotPassword(email);
  }

  /**
   * Resets the user's password using a reset token.
   * @param payload Reset password request payload.
   * @returns Observable that completes once the password is reset.
   */
  resetPassword(payload: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Observable<void> {
    return this.authAPIService.resetPassword(payload);
  }

  refreshToken(): Observable<AuthenticationResponseDto | null> {
    return this.tokenRefreshService.refreshToken({
      onSuccess: (response) => {
        this.applyAuthResponse(response);
      },
      onError: (error) => this.handleRefreshFailure(error)
    });
  }

  /**
   * Fetches fresh user data from the API and updates the auth state.
   * Call this on app initialization to ensure the stored profile is up to date.
   */
  refreshUser(): Observable<void> {
    return this.userService.getMe().pipe(
      tap((user) => this.authStorageService.updateUser(user)),
      map(() => void 0)
    );
  }

  /** Clears all authentication data from memory and localStorage. */
  closeSession() {
    // Clear local push-notification state. Server-side device-token removal is
    // handled in logout() while the session is still valid; involuntary session
    // ends (e.g. refresh failure) rely on the backend invalidating the token,
    // since the auth token here may already be dead.
    this.webPushService.cleanup();

    this._accessToken.set(null);

    // Cancel schedulings
    this.tokenRefreshService.clear();

    this.authStorageService.clearTokens();
    this.authStorageService.clearState();
    this.filterStorageService.clearAll();
  }

  /** Persists tokens and updates authentication state. */
  private applyAuthResponse(res: AuthenticationResponseDto) {
    this._accessToken.set(res.accessToken);

    this.authStorageService.saveData(res);

    this.authStorageService.decodeAndBuildState(this.accessToken, res);

    // Reschedule proactive refresh after applying new response
    this.tokenRefreshService.scheduleProactiveRefresh(res.accessToken, {
      onSuccess: (response) => this.applyAuthResponse(response),
      onError: (error) => this.handleRefreshFailure(error)
    });

    this.tokenRefreshService.setupActivityBasedRefresh(() => this.accessToken, {
      onSuccess: (response) => this.applyAuthResponse(response),
      onError: (error) => this.handleRefreshFailure(error)
    });
  }

  hasPermission(permission: Permission | Permission[]): boolean {
    //If it is a corporate staff, it will have access
    if (
      this.authState().corporateId &&
      this.authState().corporateId?.trim() !== ''
    ) {
      return true;
    }

    const userPermissions = this.authState().permissions || [];

    if (Array.isArray(permission)) {
      return permission.every((p) => userPermissions.includes(p));
    }
    return userPermissions.includes(permission);
  }
}
